import { z } from 'zod';
import { logExceptionOnce, logUnknownErrorResponse } from '@hungryroot/o11y';
import { getApiV2DynamicConfigsName, StatusCode } from '@hungryroot/rest';

const PRESET_PLAN_QUERY_PARAM = 'plan';
const PRESET_PLAN_SESSION_STORAGE_KEY = 'quiz.presetPlanCookieName';
const PRESET_PLAN_COOKIE_CONFIG =
  'api.plan_recommender.preset_plan_key_by_cookie';
const SHARED_COOKIE_DOMAIN = '.hungryroot.com';
const COOKIE_NAME_PATTERN = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;
const presetPlanCookieMapSchema = z.record(z.string());

export function capturePresetPlanOverride({
  search,
}: {
  search: string;
}): void {
  if (typeof window === 'undefined') return;

  const cookieName = new URLSearchParams(search).get(PRESET_PLAN_QUERY_PARAM);
  if (!cookieName) return;

  try {
    const previousCookieName = window.sessionStorage.getItem(
      PRESET_PLAN_SESSION_STORAGE_KEY
    );

    if (previousCookieName && previousCookieName !== cookieName) {
      removePresetPlanCookie(previousCookieName);
    }

    window.sessionStorage.setItem(PRESET_PLAN_SESSION_STORAGE_KEY, cookieName);
  } catch (error) {
    logExceptionOnce(error, {
      key: 'presetPlanOverride.captureSessionStorage',
    });
  }
}

export async function preparePresetPlanOverrideCookie(): Promise<boolean> {
  const cookieName = readPresetPlanCookieName();
  if (!cookieName) return false;

  try {
    const response = await getApiV2DynamicConfigsName({
      name: PRESET_PLAN_COOKIE_CONFIG,
    });

    if (response.status !== StatusCode.SuccessOK) {
      removePresetPlanCookie(cookieName);
      logUnknownErrorResponse('Failed to load preset plan configuration', {
        key: 'presetPlanOverride.dynamicConfigResponse',
        status: response.status,
      });
      return false;
    }

    const cookieMap = parsePresetPlanCookieMap(response.data.value);
    if (cookieMap === null) {
      clearPresetPlanOverride();
      logUnknownErrorResponse('Invalid preset plan configuration', {
        key: 'presetPlanOverride.invalidDynamicConfig',
      });
      return false;
    }

    if (
      !Object.hasOwn(cookieMap, cookieName) ||
      !COOKIE_NAME_PATTERN.test(cookieName)
    ) {
      clearPresetPlanOverride();
      return false;
    }

    writePresetPlanCookie(cookieName);
    return true;
  } catch (error) {
    removePresetPlanCookie(cookieName);
    logUnknownErrorResponse('Failed to prepare preset plan cookie', {
      key: 'presetPlanOverride.prepareCookie',
      error,
    });
    return false;
  }
}

export function hasPresetPlanOverride(): boolean {
  return readPresetPlanCookieName() !== null;
}

export function clearPresetPlanOverride(): void {
  const cookieName = readPresetPlanCookieName();
  if (cookieName) removePresetPlanCookie(cookieName);
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.removeItem(PRESET_PLAN_SESSION_STORAGE_KEY);
  } catch (error) {
    logExceptionOnce(error, {
      key: 'presetPlanOverride.clearSessionStorage',
    });
  }
}

function readPresetPlanCookieName(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    return window.sessionStorage.getItem(PRESET_PLAN_SESSION_STORAGE_KEY);
  } catch (error) {
    logExceptionOnce(error, {
      key: 'presetPlanOverride.readSessionStorage',
    });
    return null;
  }
}

function parsePresetPlanCookieMap(
  value?: string
): Record<string, string> | null {
  if (!value) return null;

  try {
    const result = presetPlanCookieMapSchema.safeParse(JSON.parse(value));
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

function writePresetPlanCookie(cookieName: string): void {
  if (typeof document === 'undefined') return;

  document.cookie = `${cookieName}=${encodeURIComponent(
    cookieName
  )}; Domain=${SHARED_COOKIE_DOMAIN}; Path=/; SameSite=Lax; Secure`;
}

function removePresetPlanCookie(cookieName: string): void {
  if (typeof document === 'undefined') return;
  if (!COOKIE_NAME_PATTERN.test(cookieName)) return;

  document.cookie = `${cookieName}=; Domain=${SHARED_COOKIE_DOMAIN}; Path=/; Max-Age=0; SameSite=Lax; Secure`;
}
